import {
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText
} from '@material-ui/core';
import React, { FC, useEffect, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { isEmpty } from 'lodash';
import { OrderingBizHour } from 'kiolyn-api';
import { defaultTimezone } from '../../../../domain/models/Constants';
import { PauseOrderTypes } from '../../../../domain/models/PauseOrderTypes';

interface IProps {
  label: string;
  disabled?: boolean;
  helperText?: string;
  orderType?: string;
  error: boolean;
  value?: Date;
  delayTime: number;
  bizHours?: OrderingBizHour[];
  timezone?: string;
  onChange?: (d: moment.Moment) => void;
  pauseOrder?: PauseOrderTypes;
}
let timezone: string;

function momentByTimezone(date?: Date | string) {
  return moment(date).tz(timezone);
}

function isSameCurrentDate(date: moment.Moment) {
  return date.startOf('day').isSame(momentByTimezone().startOf('day'));
}

function getDate(date?: Date) {
  if (isSameCurrentDate(momentByTimezone(date))) {
    return momentByTimezone();
  }
  return momentByTimezone(date).startOf('day');
}

function isNeedGenerate(data: moment.Moment[], date?: Date) {
  return data.length > 0
    ? !momentByTimezone(date)
        .startOf('day')
        .isSame(data[0].clone().startOf('day'))
    : true;
}

function parseTimeSlot(
  now: moment.Moment,
  delayTime?: number,
  bizHours?: OrderingBizHour[],
  pauseOrder?: PauseOrderTypes
) {
  now.tz(timezone);
  if (now.isBefore(momentByTimezone())) {
    return [];
  }

  const period = 15;
  const generate = (from?: string[], to?: string[]) => {
    const list = [];
    let startBusinessTime = now.clone();

    let pauseOrderTo = now.clone();
    if (
      pauseOrder !== undefined &&
      pauseOrder !== PauseOrderTypes.NONE &&
      pauseOrder !== PauseOrderTypes.ONEDAY
    ) {
      pauseOrderTo.add(pauseOrder, 'h');
    }

    if (from) {
      startBusinessTime
        .hour(parseInt(from[0]))
        .minutes(parseInt(from[1]))
        .startOf('minute');
    }
    let endBusinessTime = now.clone();

    if (to) {
      endBusinessTime
        .hour(parseInt(to[0]))
        .minutes(parseInt(to[1]))
        .startOf('minute');
    } else {
      endBusinessTime.endOf('day');
    }
    let startTime = (pauseOrderTo.isAfter(startBusinessTime)
      ? pauseOrderTo
      : startBusinessTime.clone()
    )
      .add(delayTime, 'minutes')
      .startOf('minute');
    if (isSameCurrentDate(startTime.clone())) {
      const numberDivisible = startTime.clone().minute() % 5;
      const remainder = numberDivisible === 0 ? 0 : 5 - numberDivisible;
      startTime = startTime.add(5 + remainder, 'minute');
    }
    if (startTime.isAfter(endBusinessTime)) {
      return [];
    }

    const endTime = endBusinessTime
      .subtract(delayTime, 'minutes')
      .startOf('minute');
    if (isEmpty(endTime)) {
      return [];
    }
    list.push(startTime.clone());

    while (startTime.isSameOrBefore(endTime)) {
      list.push(startTime.add(period, 'minutes').clone());
    }
    list.splice(-1);
    if (isSameCurrentDate(startTime)) {
      return list.filter(e =>
        e
          .clone()
          // .tz(moment.tz.guess())
          .isSame(momentByTimezone(), 'date')
      );
    }
    return list;
  };

  const currentBizHours = bizHours?.find(e => e.day_of_week === now.weekday());
  if (currentBizHours) {
    if (!currentBizHours.is_working) {
      return [];
    }
    return [
      {
        from: currentBizHours.from_1,
        to: currentBizHours.to_1
      },
      {
        from: currentBizHours.from_2,
        to: currentBizHours.to_2
      }
    ]
      .filter(e => e.from && e.to)
      .map(e => generate(e?.from?.split(':'), e?.to?.split(':')))
      .reduce((a, b) => a.concat(b), []);
  }

  return generate();
}

function usePrevious<V = any>(value: V) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value as any;
  });
  return ref.current;
}

const TimeSlotOrder: FC<IProps> = props => {
  timezone = props.timezone || defaultTimezone;
  const [data, setData] = useState<moment.Moment[]>([]);
  const [value, setValue] = useState(props.value ?? '');
  const getDefaultValue = (current: moment.Moment[]) => {
    if (props.value) {
      if (!props.pauseOrder) {
        return (
          current
            .find(e => e.isSame(momentByTimezone(props?.value)))
            ?.clone() ?? current[0].clone()
        );
      }

      const index = current.findIndex(e =>
        e.isAfter(momentByTimezone(props?.value))
      );
      if (index >= 0) {
        return current[index].clone();
      }
    }
    return current[0].clone();
  };
  const { orderType } = props;
  const prevOrderType = usePrevious(orderType);

  useEffect(() => {
    if (prevOrderType && props.orderType !== prevOrderType) {
      const current = parseTimeSlot(
        getDate(props.value),
        props.delayTime,
        props.bizHours,
        props.pauseOrder
      );
      setData(current);
      if (current.length > 0) {
        setValue(current[0].clone().toString());
        props?.onChange?.(current[0].clone());
      }
      return;
    }

    if (isNeedGenerate(data, props.value)) {
      const current = parseTimeSlot(
        getDate(props.value),
        props.delayTime,
        props.bizHours,
        props.pauseOrder
      );
      setData(current);
      if (current.length > 0) {
        setValue(current[0].clone().toString());
        props?.onChange?.(getDefaultValue(current));
      }
      return;
    }

    if (
      props.value &&
      !momentByTimezone(props.value).isSame(momentByTimezone(value))
    ) {
      return setValue(getDefaultValue(data).toString());
    }
    // eslint-disable-next-line
  }, [props.value, props.orderType]);

  return (
    <FormControl error={props.error}>
      <InputLabel>{props.label}</InputLabel>
      <Select
        onChange={e => {
          props.onChange?.(momentByTimezone(e.target.value as string));
        }}
        inputProps={{ readOnly: props.disabled }}
        disabled={data.length === 0}
        value={value}
      >
        {data.map((e, i) => (
          <MenuItem key={i} value={e.toString()}>
            {e
              .clone()
              // .tz(moment.tz.guess())
              .format('hh:mm A')}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>{props.helperText}</FormHelperText>
    </FormControl>
  );
};

export default TimeSlotOrder;
